import { useState, useMemo } from "react"
import { cn } from "../../lib/utils"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import TranscriptPanel from "./TranscriptPanel"
import type { TranscriptChunk } from "../../contexts/EncounterContext"
import { Pencil, Link2 } from "lucide-react"

interface SourceGrounding {
  noteStart: number
  noteEnd: number
  transcriptStart: number
  transcriptEnd: number
  groundingType: "extraction" | "inference" | "template"
  confidenceScore: number
}

interface NoteWithSourcesProps {
  noteContent: string
  transcript: TranscriptChunk[]
  groundings: SourceGrounding[]
  onEditNote?: () => void
}

const GROUNDING_STYLES = {
  extraction: { color: "bg-blue-200/50 dark:bg-blue-800/30", border: "border-blue-400", label: "Extracted", badge: "bg-blue-100 text-blue-700" },
  inference: { color: "bg-amber-200/50 dark:bg-amber-800/30", border: "border-amber-400", label: "Inferred", badge: "bg-amber-100 text-amber-700" },
  template: { color: "bg-gray-200/50 dark:bg-gray-800/30", border: "border-gray-400", label: "Template", badge: "bg-gray-100 text-gray-700" },
} as const

const NoteWithSources = ({
  noteContent,
  transcript,
  groundings,
  onEditNote,
}: NoteWithSourcesProps) => {
  const [activeGrounding, setActiveGrounding] = useState<SourceGrounding | null>(null)
  const [mobileView, setMobileView] = useState<'note' | 'transcript'>('note')

  const transcriptHighlight = useMemo(() => {
    if (!activeGrounding) return undefined
    return { start: activeGrounding.transcriptStart, end: activeGrounding.transcriptEnd }
  }, [activeGrounding])

  // Build note segments with grounding markup
  const segments = useMemo(() => {
    if (groundings.length === 0) return [{ text: noteContent, grounding: null }]

    const sorted = [...groundings].sort((a, b) => a.noteStart - b.noteStart)
    const result: { text: string; grounding: SourceGrounding | null }[] = []
    let cursor = 0

    for (const g of sorted) {
      // Skip fully covered groundings
      if (g.noteEnd <= cursor) continue
      // Clamp partially overlapping groundings
      const start = Math.max(g.noteStart, cursor)
      if (start > cursor) {
        result.push({ text: noteContent.slice(cursor, start), grounding: null })
      }
      result.push({ text: noteContent.slice(start, g.noteEnd), grounding: g })
      cursor = g.noteEnd
    }
    if (cursor < noteContent.length) {
      result.push({ text: noteContent.slice(cursor), grounding: null })
    }
    return result
  }, [noteContent, groundings])

  const notePane = (
    <div className="flex-1 flex flex-col min-w-0 md:border-r">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          Generated Note
        </div>
        <div className="flex items-center gap-2">
          {activeGrounding && (
            <Badge className={cn("text-xs", GROUNDING_STYLES[activeGrounding.groundingType].badge)}>
              {GROUNDING_STYLES[activeGrounding.groundingType].label}
              {" "}({Math.round(activeGrounding.confidenceScore * 100)}%)
            </Badge>
          )}
          {onEditNote && (
            <Button variant="ghost" size="sm" onClick={onEditNote}>
              <Pencil className="h-3.5 w-3.5 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 text-sm leading-relaxed whitespace-pre-wrap">
        {segments.map((seg, i) =>
          seg.grounding ? (
            <span
              key={i}
              onClick={() => setActiveGrounding(seg.grounding === activeGrounding ? null : seg.grounding)}
              className={cn(
                "cursor-pointer rounded px-0.5 border-b-2 transition-colors",
                GROUNDING_STYLES[seg.grounding.groundingType].color,
                GROUNDING_STYLES[seg.grounding.groundingType].border,
                seg.grounding === activeGrounding && "ring-2 ring-blue-500/40"
              )}
            >
              {seg.text}
            </span>
          ) : (
            <span key={i}>{seg.text}</span>
          )
        )}
      </div>
    </div>
  )

  const transcriptPane = (
    <div className="flex-1 md:w-80 md:flex-none flex flex-col min-w-0 shrink-0">
      <div className="hidden md:block border-b px-4 py-2 text-sm font-medium">
        Source Transcript
      </div>
      <div className="flex-1 overflow-hidden">
        <TranscriptPanel
          chunks={transcript}
          highlightRange={transcriptHighlight}
          autoScroll={false}
        />
      </div>
    </div>
  )

  return (
    <div className="flex flex-col md:flex-row h-full border rounded-lg overflow-hidden">
      {/* Mobile tab bar */}
      <div className="flex md:hidden border-b">
        <button
          onClick={() => setMobileView('note')}
          className={cn(
            "flex-1 px-4 py-2.5 text-sm font-medium text-center min-h-[44px] transition-colors",
            mobileView === 'note'
              ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
              : "text-muted-foreground"
          )}
        >
          Note
        </button>
        <button
          onClick={() => setMobileView('transcript')}
          className={cn(
            "flex-1 px-4 py-2.5 text-sm font-medium text-center min-h-[44px] transition-colors",
            mobileView === 'transcript'
              ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
              : "text-muted-foreground"
          )}
        >
          Transcript
        </button>
      </div>

      {/* Mobile: show selected view only */}
      <div className="flex-1 flex flex-col md:hidden overflow-hidden">
        {mobileView === 'note' ? notePane : transcriptPane}
      </div>

      {/* Desktop: side-by-side */}
      <div className="hidden md:flex md:flex-1 md:flex-row overflow-hidden">
        {notePane}
        {transcriptPane}
      </div>
    </div>
  )
}

export default NoteWithSources
